import useSWR from "swr";
import {
  EANStatus,
  LoadData,
  LogOptions,
  checkEANStatus,
  getSkinMatchIngredientURL,
  getSkinMatchIngredientsData,
} from "./skinMatchService";
import { SkinMatchIngredientData } from "./skinMatchSchema";
import { z } from "zod";
import { noticeError } from "../../utils/newrelic/requests";
import { getErrorMessage, zodIssuesParser } from "./util";

function useSkinMatchStatus(
  ean: string,
  skinMatchLookUpUrl: string,
): LoadData<EANStatus> {
  const lookUpUrl = `https://${skinMatchLookUpUrl}/ean/${ean}`;
  const { data: skinMatchResp, error } = useSWR(
    ean ? lookUpUrl : null,
    checkEANStatus,
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );
  if (!ean) {
    return { status: "No Action" };
  }
  if (error) {
    return {
      status: "Error",
      message: "Error getting skin-match status",
    };
  }
  if (!skinMatchResp) {
    return { status: "Loading" };
  }
  return { status: "Done", data: skinMatchResp };
}

function useSkinMatchIngredients(
  ean: string,
  skinMatchIngredientBasePath: string,
  pimProductId: string,
  variantId: string,
): LoadData<SkinMatchIngredientData> {
  const ingredientsUrl = getSkinMatchIngredientURL(
    ean,
    skinMatchIngredientBasePath,
  );
  const { data: ingredientData, error } = useSWR<SkinMatchIngredientData>(
    ean ? ingredientsUrl : null,
    getSkinMatchIngredientsData,
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      shouldRetryOnError: false,
      revalidateOnReconnect: false,
    },
  );

  if (!ean) {
    return { status: "No Action" };
  }
  if (error) {
    const logOptions: LogOptions = {
      ean,
      productId: pimProductId,
      variantId: variantId,
    };
    if (error instanceof z.ZodError) {
      const issues = zodIssuesParser(error);
      const customMessage = `SKIN_MATCH_API_SCHEMA_ERROR: For current productID: ${logOptions.productId} 
        with variant ean: ${logOptions.ean}, SkinMatch Ingredients API response doesn't have right schema. ${issues}`;
      noticeError(customMessage, logOptions);
    } else {
      const customMessage = `SKIN_MATCH_API_NETWORK_ERROR: For current productID: ${
        logOptions.productId
      } with variant ean: ${logOptions.ean}: ${getErrorMessage(error)}`;
      noticeError(customMessage, logOptions);
    }
    return {
      status: "Error",
      message: "Error getting skin-match ingredient data",
    };
  }
  if (!ingredientData) {
    return { status: "Loading" };
  }
  return { status: "Done", data: ingredientData };
}

export { useSkinMatchStatus, useSkinMatchIngredients };
